import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button, StatCard, Table, useTable } from "@repo/o37-ui";
import {
  Plus,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Clock,
  CreditCard,
  Building,
  Settings,
  RefreshCw,
  Link2
} from "lucide-react";
import { HoldingsBankAccount, HoldingsStatementImport } from '../../types';
import BankAccountForm from '../../components/Holdings/BankAccountForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function BankImportDashboard() {
  const navigate = useNavigate();
  const [bankAccounts, setBankAccounts] = useState<HoldingsBankAccount[]>([]);
  const [imports, setImports] = useState<HoldingsStatementImport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<HoldingsBankAccount | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [accountsRes, importsRes] = await Promise.all([
        fetch(`${API_URL}/holdings/bank-accounts`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/imports`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (accountsRes.ok) {
        const data = await accountsRes.json();
        setBankAccounts(data.map((a: any) => ({
          id: a.id,
          name: a.name,
          accountType: a.account_type,
          lastFour: a.last_four,
          bankName: a.bank_name,
          isActive: a.is_active,
          notes: a.notes,
          createdAt: a.created_at,
          provider: a.provider,
          mercuryAccountId: a.mercury_account_id,
          lastSyncedAt: a.last_synced_at
        })));
      }

      if (importsRes.ok) {
        const data = await importsRes.json();
        setImports(data.map((i: any) => ({
          id: i.id,
          bankAccountId: i.bank_account_id,
          fileName: i.file_name,
          fileType: i.file_type,
          status: i.status,
          totalTransactions: i.total_transactions,
          matchedTransactions: i.matched_transactions,
          unmatchedTransactions: i.unmatched_transactions,
          errorMessage: i.error_message,
          processedAt: i.processed_at,
          createdAt: i.created_at,
          bankAccount: i.holdings_bank_accounts ? {
            id: i.holdings_bank_accounts.id,
            name: i.holdings_bank_accounts.name
          } : undefined
        })));
      }
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-amber-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-neutral-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-100 text-emerald-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      case 'processing':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-neutral-100 text-neutral-600';
    }
  };

  const activeAccounts = bankAccounts.filter(a => a.isActive);
  const totalTransactions = imports.reduce((sum, i) => sum + i.totalTransactions, 0);
  const unmatchedCount = imports.reduce((sum, i) => sum + i.unmatchedTransactions, 0);

  const handleMercurySync = async (accountId: string) => {
    setSyncing(accountId);
    setSyncMessage(null);

    try {
      const response = await fetch(`${API_URL}/holdings/bank-accounts/${accountId}/mercury/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSyncMessage({
          type: 'success',
          text: data.message || `Imported ${data.imported} transactions`
        });
        loadData(); // Reload to show new imports
      } else {
        setSyncMessage({
          type: 'error',
          text: data.error || 'Sync failed'
        });
      }
    } catch (e) {
      setSyncMessage({
        type: 'error',
        text: 'Failed to sync with Mercury'
      });
    } finally {
      setSyncing(null);
    }
  };

  const importColumns = useMemo(() => [
    {
      id: "file",
      header: "File",
      cell: ({ row }: { row: { original: HoldingsStatementImport } }) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5 text-neutral-500" />
          </div>
          <div>
            <div className="font-medium text-neutral-900">{row.original.fileName}</div>
            <div className="text-sm text-neutral-500">
              {row.original.bankAccount?.name || 'No account'} • {row.original.fileType.toUpperCase()}
            </div>
          </div>
        </div>
      )
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }: { row: { original: HoldingsStatementImport } }) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(row.original.status)}
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(row.original.status)}`}>
            {row.original.status.charAt(0).toUpperCase() + row.original.status.slice(1)}
          </span>
        </div>
      )
    },
    {
      id: "transactions",
      header: "Transactions",
      cell: ({ row }: { row: { original: HoldingsStatementImport } }) => (
        <div className="text-sm">
          <div className="font-medium text-neutral-900">{row.original.totalTransactions} total</div>
          <div className="text-neutral-500">
            {row.original.matchedTransactions} matched, {row.original.unmatchedTransactions} unmatched
          </div>
        </div>
      )
    },
    {
      id: "date",
      header: "Imported",
      cell: ({ row }: { row: { original: HoldingsStatementImport } }) => (
        <span className="text-sm text-neutral-500">
          {formatDate(row.original.createdAt)}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }: { row: { original: HoldingsStatementImport } }) => (
        row.original.unmatchedTransactions > 0 && row.original.status === 'completed' ? (
          <Button
            variant="outline"
            className="h-8 text-xs"
            onClick={() => navigate(`/holdings/bank-imports/review?import=${row.original.id}`)}
            text="Review"
          />
        ) : null
      )
    }
  ], [navigate]);

  const { table: importsTable, ...importsTableProps } = useTable({
    data: imports,
    columns: importColumns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: imports.length,
    resourceName: (plural) => `import${plural ? "s" : ""}`,
  });

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Bank Statement Import</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Bank Statement Import</h1>
          <p className="text-neutral-500">Import and match bank transactions</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/holdings/bank-imports/mappings')}
            icon={<Settings className="w-4 h-4" />}
            text="Vendor Mappings"
          />
          <Button
            variant="outline"
            onClick={() => navigate('/holdings/bank-imports/mercury')}
            icon={<Link2 className="w-4 h-4" />}
            text="Connect Mercury"
          />
          <Button
            onClick={() => navigate('/holdings/bank-imports/upload')}
            icon={<Upload className="w-4 h-4" />}
            text="Import Statement"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Bank Accounts"
          value={activeAccounts.length.toString()}
          hint={`${bankAccounts.length} total`}
        />
        <StatCard
          label="Imports"
          value={imports.length.toString()}
          hint="Statement files"
        />
        <StatCard
          label="Transactions"
          value={totalTransactions.toString()}
          hint="Total imported"
        />
        <StatCard
          label="Needs Review"
          value={unmatchedCount.toString()}
          hint="Unmatched transactions"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bank Accounts */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Bank Accounts</h2>
              <Button
                variant="outline"
                className="h-8 text-xs"
                onClick={() => {
                  setEditingAccount(null);
                  setShowAccountForm(true);
                }}
                icon={<Plus className="w-3.5 h-3.5" />}
                text="Add"
              />
            </div>

            {/* Sync Message */}
            {syncMessage && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                syncMessage.type === 'success'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {syncMessage.text}
              </div>
            )}

            {bankAccounts.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Building className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p className="mb-4">No bank accounts yet</p>
                <Button
                  onClick={() => setShowAccountForm(true)}
                  text="Add Account"
                />
              </div>
            ) : (
              <div className="space-y-3">
                {bankAccounts.map((account) => {
                  const isMercury = (account as any).provider === 'mercury' && (account as any).mercuryAccountId;
                  const isSyncing = syncing === account.id;

                  return (
                    <div
                      key={account.id}
                      className="p-3 bg-neutral-50 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${
                            isMercury ? 'bg-blue-50 border-blue-200' : 'bg-white border-neutral-200'
                          }`}>
                            {isMercury ? (
                              <Link2 className="w-5 h-5 text-blue-600" />
                            ) : (
                              <CreditCard className="w-5 h-5 text-neutral-500" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-neutral-900">{account.name}</span>
                              {isMercury && (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                                  Mercury
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-neutral-500">
                              {account.bankName || account.accountType}
                              {account.lastFour && ` •••• ${account.lastFour}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!account.isActive && (
                            <span className="text-xs px-2 py-1 bg-neutral-200 text-neutral-600 rounded-full">
                              Inactive
                            </span>
                          )}
                          <Button
                            variant="outline"
                            className="h-7 text-xs px-2"
                            onClick={() => {
                              setEditingAccount(account);
                              setShowAccountForm(true);
                            }}
                            text="Edit"
                          />
                        </div>
                      </div>

                      {/* Mercury Sync Button */}
                      {isMercury && (
                        <div className="mt-3 pt-3 border-t border-neutral-200 flex items-center justify-between">
                          <div className="text-xs text-neutral-500">
                            {(account as any).lastSyncedAt ? (
                              <>Last synced: {new Date((account as any).lastSyncedAt).toLocaleDateString()}</>
                            ) : (
                              <>Never synced</>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => handleMercurySync(account.id)}
                            disabled={isSyncing}
                            icon={<RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />}
                            text={isSyncing ? 'Syncing...' : 'Sync Now'}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Imports */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Recent Imports</h2>
                {unmatchedCount > 0 && (
                  <Button
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => navigate('/holdings/bank-imports/review')}
                    text={`Review ${unmatchedCount} Unmatched`}
                  />
                )}
              </div>

              {imports.length === 0 ? (
                <div className="text-center py-12 text-neutral-500">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
                  <h3 className="text-lg font-medium mb-2">No Imports Yet</h3>
                  <p className="mb-6">Upload your first bank statement to get started.</p>
                  <Button
                    onClick={() => navigate('/holdings/bank-imports/upload')}
                    icon={<Upload className="w-4 h-4" />}
                    text="Import Statement"
                  />
                </div>
              ) : (
                <Table {...importsTableProps} table={importsTable} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bank Account Form Modal */}
      {showAccountForm && (
        <BankAccountForm
          account={editingAccount}
          onClose={() => {
            setShowAccountForm(false);
            setEditingAccount(null);
          }}
          onSaved={() => {
            setShowAccountForm(false);
            setEditingAccount(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}
